"use client";

import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import TableFormatter from "@/components/organisms/admin/TableFormatter";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { ColorPicker } from "@/components/atoms/colorpicker";
import type { Category, MetadataItem } from "../types";

interface CategoryManagerProps {
  categories: Category[];
  categoryMetadataFields: any[];
  onAddCategory: () => void;
  onDeleteCategory: (index: number) => void;
  onUpdateCategory: (index: number, field: string, value: any) => void;
  onUpdateCategoryMetadata: (index: number, data: MetadataItem[]) => void;
  /** Merge a partial object (from the per-category config FieldsFormatter) into a category. */
  onUpdateCategoryFields?: (index: number, partial: Record<string, any>) => void;
  /** Optional per-category config fields (e.g. scroll speed/direction). */
  categoryFieldsConfig?: any[];
  /** Key under which each category stores its item array. Defaults to "metadata". */
  itemsKey?: string;
  /** Show the fee-style colour pickers. Defaults to true (preserves fee behaviour). */
  showColors?: boolean;
  titleLabel?: string;
  itemsLabel?: string;
  itemsHint?: string;
  addLabel?: string;
  categorySingularLabel?: string;
}

function CategoryManager({
  categories,
  categoryMetadataFields,
  onAddCategory,
  onDeleteCategory,
  onUpdateCategory,
  onUpdateCategoryMetadata,
  onUpdateCategoryFields,
  categoryFieldsConfig,
  itemsKey = "metadata",
  showColors = true,
  titleLabel = "Tab Title",
  itemsLabel = "Fee Rows",
  itemsHint = 'toggle "Is Total" for summary rows',
  addLabel = "Add Category",
  categorySingularLabel = "Category",
}: CategoryManagerProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    categories.length > 0 ? 0 : null
  );

  return (
    <div className="space-y-3">
      {categories.map((category, index) => {
        const isExpanded = expandedIndex === index;
        const items = (category[itemsKey] as any[]) || [];

        return (
          <div
            key={category.id || index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Category Header */}
            <div
              className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="size-4 text-gray-500" />
                ) : (
                  <ChevronRight className="size-4 text-gray-500" />
                )}
                <span className="font-medium text-sm">
                  {category.title || `${categorySingularLabel} ${index + 1}`}
                </span>
                <span className="text-xs text-gray-400">
                  ({items.length} rows)
                </span>
              </div>
              <button
                className="text-red-500 hover:text-red-700 p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteCategory(index);
                }}
              >
                <Trash2 className="size-4" />
              </button>
            </div>

            {/* Category Content */}
            {isExpanded && (
              <div className="p-4 space-y-4">
                {/* Category Settings */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">
                      {titleLabel}
                    </label>
                    <input
                      type="text"
                      value={category.title}
                      onChange={(e) =>
                        onUpdateCategory(index, "title", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                      placeholder="e.g. Annual"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">
                      Order
                    </label>
                    <input
                      type="number"
                      value={category.order_index ?? index}
                      onChange={(e) =>
                        onUpdateCategory(
                          index,
                          "order_index",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                {/* Config-driven per-category fields (e.g. scroll speed/direction) */}
                {categoryFieldsConfig && categoryFieldsConfig.length > 0 && (
                  <FieldsFormatter
                    data={category}
                    fields={categoryFieldsConfig}
                    updateParent={true}
                    onChange={(updated: Record<string, any>) =>
                      onUpdateCategoryFields?.(index, updated)
                    }
                    className="p-0 bg-transparent"
                  />
                )}

                {/* Category Colors */}
                {showColors && (
                  <div className="flex flex-wrap gap-4">
                    <ColorPicker
                      label="Text Color"
                      value={category.text_color || "#000000"}
                      onChange={(val: string) =>
                        onUpdateCategory(index, "text_color", val)
                      }
                    />
                    <ColorPicker
                      label="BG Color"
                      value={category.bg_color || "transparent"}
                      onChange={(val: string) =>
                        onUpdateCategory(index, "bg_color", val)
                      }
                    />
                    <ColorPicker
                      label="Active Text"
                      value={category.active_text_color || "#ffffff"}
                      onChange={(val: string) =>
                        onUpdateCategory(index, "active_text_color", val)
                      }
                    />
                    <ColorPicker
                      label="Active BG"
                      value={category.active_bg_color || "#02a7b6"}
                      onChange={(val: string) =>
                        onUpdateCategory(index, "active_bg_color", val)
                      }
                    />
                    <ColorPicker
                      label="Border"
                      value={category.border_color || "rgba(0,0,0,0.4)"}
                      onChange={(val: string) =>
                        onUpdateCategory(index, "border_color", val)
                      }
                    />
                  </div>
                )}

                {/* Category items */}
                <div className="border-t pt-3">
                  <h5 className="text-xs font-semibold text-gray-600 uppercase mb-2">
                    {itemsLabel}
                    {itemsHint && (
                      <span className="font-normal normal-case text-gray-400 ml-1">
                        ({itemsHint})
                      </span>
                    )}
                  </h5>
                  <TableFormatter
                    data={items}
                    fields={categoryMetadataFields}
                    onDataUpdate={(data) =>
                      onUpdateCategoryMetadata(index, data as MetadataItem[])
                    }
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add Category Button */}
      <Button
        classType="admin"
        className="flex items-center gap-2"
        onClick={onAddCategory}
      >
        <Plus className="size-4" />
        {addLabel}
      </Button>
    </div>
  );
}

export default CategoryManager;
